/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { SegmentType } from '@ve-pro/enums/segment'
import { VideoEventter } from '@ve-pro/ve/utils/event-bus'
import type {
  SegmentCollection,
  SegmentMaterial,
} from '@ve-pro/types/track.type'
import { getTranscodeStatus } from '@pt/api/assets/index'
import {
  selectAsset,
  removeAsset,
  multipleState,
} from '@com/components/batch-add-asset-dialog/index'
import { resolveSegmentText } from '@ve-pro/store/tracks/resolve-segment'
import { resolveSegment } from '@ve-pro/store/tracks/resolve-segment'
import {
  getProjectAssets,
  getAssetProgress,
  getAssetAdd,
  getAssetChoose,
  getAsset,
} from '@ve-pro/api/aside'

import type {
  AsssetsModel,
  ProjectAssetsModel,
  GetProjectAssetsParams,
  AssetAddParams,
  TransProgress,
} from '@ve-pro/api/aside/type'
import type { FileListTypeProperty } from '@ve-pro/ve/aside/config/aside-type'
import { useTrackStore, useProjectStore } from '@ve-pro/store'
import {
  addSegmentbyCurrentTime,
  addSegmentTransition,
} from '@ve-pro/store/tracks/add-segment-action'
import { AssetType, AssetSource } from '@ve-pro/enums/asset'
import { getSegmentTransition } from './resolve-preview-segment'
import { isSegmentGroup } from '@ve-pro/store/tracks/segment/segment-group'
import { ProjectMode } from '@ve-pro/enums/project'
import { AssetStatus } from '@pt/enums/assets'
import { AsideType } from '@ve-pro/ve/aside/config/aside-enum'
import { AssetsTransProgress } from '@pt/api/assets/index.type'
import type {
  ReplaceUploadGroup,
  ResourceSummaryProperty,
} from '@ve-pro/api/asset-group/type'
import { t } from '@ve-pro/ve/lang'

// 项目资源列表
export function handleUploadFolderList(assetsParams: GetProjectAssetsParams) {
  return getProjectAssets(assetsParams)
}

// 转码进度列表
export function handleAssetProgress(idsArray: any[]) {
  const params = { ids: idsArray.toString() }
  return getAssetProgress(params)
}

export function seeMaterial(file: ProjectAssetsModel): SegmentMaterial {
  return {
    ...file,
  }
}

// 数据类型对应的中文描述
export function getAssetText(val: AssetType | string): string {
  switch (val) {
    case AssetType.Video:
      return t('aside.menu.material.video')
    case AssetType.Audio:
      return t('aside.menu.material.audio')
    case AssetType.Image:
      return t('aside.menu.material.image')
    case AssetType.Text:
      return t('aside.menu.text.label')
    default:
      return ''
  }
}

export function handleAddResourceAndReturnRes(
  id: number | string,
  source?: AssetSource | AsideType,
  is_original?: boolean,
): ReturnType<typeof getAsset> {
  const projectStore = useProjectStore()
  const params: AssetAddParams = {
    ids: [id],
    project_id: projectStore.id,
  }

  // 上传文件同步到项目素材需要增加此参数
  if (is_original) {
    params.is_original = is_original
  }
  // 是否来自资产库
  const isAssetSource =
    typeof source !== 'undefined' && source === AssetSource.Asset

  if (isAssetSource) {
    return getAssetChoose(params)
  }
  return getAssetAdd(params)
}
// 资源添加接口
export async function handleAddResource(
  id: number | string,
  source?: AssetSource,
  is_original?: boolean,
): Promise<ProjectAssetsModel | null> {
  const res = await handleAddResourceAndReturnRes(id, source, is_original)

  return res.data.data.assets?.[0] ?? null
}

// 资源预览
export function handlePreviewFile(
  materialType: SegmentMaterial | SegmentType.Text,
): void {
  const isText = materialType === SegmentType.Text

  let segment: SegmentCollection | null
  if (isText) {
    segment = resolveSegmentText()
  } else if (materialType.type === AssetType.Transition) {
    segment = getSegmentTransition(materialType)
  } else {
    segment = resolveSegment(materialType)
  }

  if (segment) {
    VideoEventter.emit('preview', segment)
  }
}

// 资源已添加
// 资源、组里面的子资源都要！！！
export function handleAdded(
  id: ProjectAssetsModel['asset_id'] | AsssetsModel['id'],
): boolean {
  const trackStore = useTrackStore()
  const _segments = trackStore.segments.filter((s) => !isSegmentGroup(s))

  const segments = Object.entries(trackStore.segmentGroups)
    .filter(([id]) => trackStore.segments.some((s) => s.id === Number(id)))
    .reduce(
      (result, [id, children]) => {
        result.push(...children)

        return result
      },
      [..._segments],
    )

  return segments.some((s) => {
    if (s?.material?.source === AssetSource.User) {
      return s.asset_id === id
    } else if (
      s?.material?.source === AssetSource.Asset ||
      s?.material?.source === AssetSource.Template
    ) {
      return s.asset_id === id || s?.material?.source_id === id
    }

    return String(s?.material?.source_id) === String(id)
  })
}

// 添加资源到轨道
export async function handleAddToTimeLine(
  asset: ProjectAssetsModel,
): Promise<void> {
  if (asset.type === AssetType.Transition) {
    await addSegmentTransition(asset)
  } else {
    await addSegmentbyCurrentTime(asset, {
      stage: ProjectMode.Default,
    })
  }
}

// 获取转码的id
export function getTransCodeAddPendingId(
  listData: ProjectAssetsModel[] | ReplaceUploadGroup[],
) {
  const transCodeId: string[] = []
  const publicTransCodeId: string[] = []

  listData.forEach((item) => {
    if (
      item.status === AssetStatus.Transcoding ||
      item.status === AssetStatus.Pending
    ) {
      // 公共库转码id
      if (item.source === AssetSource.Public) {
        publicTransCodeId.push(String(item.asset_id))
      } else {
        // 项目素材｜资产库转码id
        transCodeId.push(String(item.source_id))
      }
    }
  })
  return { transCodeId, publicTransCodeId }
}

// 根据状态是否需要更新列表
export async function getTranscodeProgress(
  transCodeId: string[],
  publicTransCodeId?: string[],
): Promise<{ data: TransProgress[] | AssetsTransProgress[]; update: boolean }> {
  const res =
    publicTransCodeId && publicTransCodeId.length
      ? await getAssetProgress({ ids: publicTransCodeId.join(',') }) // 公共库转码接口
      : await getTranscodeStatus(transCodeId.join(',')) // 项目素材｜资产库转码接口

  const data = res.data.data ?? []

  // 需要更新列表数据
  const update = data?.some((item: TransProgress | AssetsTransProgress) => {
    return (
      item.status === AssetStatus.Active || item.status === AssetStatus.Error
    )
  })

  return { data, update }
}

// 资源转码进度
export function updateProgress(
  list: ProjectAssetsModel[] | null,
  data: TransProgress[] | AssetsTransProgress[],
) {
  if (list?.length) {
    return list.map((value: ProjectAssetsModel) => {
      const _index = data.findIndex(
        (c: TransProgress | AssetsTransProgress) => {
          // 公共库
          if ('id' in c && c.id) {
            return c.id === value.asset_id
          }
          // 项目素材｜资产库
          if ('asset_id' in c && c.asset_id) {
            return c.asset_id === String(value.source_id)
          }
        },
      )
      if (_index !== -1 && data.length > 0) {
        value.progress = data[_index]?.progress ?? 0
      } else {
        // data 为null时增加占位
        value.progress = 0
      }
      return { ...value }
    })
  }
  return null
}

/*
accepts:string[]  允许本地文件上传的数据类型 如 ['mp4', 'png']
*/
// 增加全局上传方法
export const handleUploadFile = (accepts: string[]) => {
  const newAccepts = accepts.map((item) => (item = '.' + item))

  if (document.querySelector('#global-ve-uploader input[type=file]')) {
    const uploaderInput = document.querySelector(
      '#global-ve-uploader input[type=file]',
    )
    if (uploaderInput) {
      uploaderInput.setAttribute('accept', newAccepts.toString())
      // @ts-ignore
      uploaderInput.click()
    }
  }
}

// 资产组弹窗资产选中事件
export function handleClickResources(file: ProjectAssetsModel, convert = true) {
  if (convert) {
    if (
      file.status !== AssetStatus.Transcoding &&
      file.status !== AssetStatus.Pending &&
      file.status !== AssetStatus.Error
    ) {
      const index = multipleState.data.findIndex(
        (val) => val.source_id === file.source_id,
      )
      if (index !== -1) {
        removeAsset(file, convert)
      } else {
        selectAsset(file, convert)
      }
    }
  } else {
    const index = multipleState.data.findIndex((val) => val.id === file.id)
    if (index !== -1) {
      removeAsset(file, convert)
    } else {
      selectAsset(file, convert)
    }
  }
}

// 检测资产组中资产的添加次数和已添加的资产名称
export function handleResourceSummary(
  summaryProperty: ResourceSummaryProperty[],
  file: FileListTypeProperty,
  convert = true,
) {
  if (summaryProperty?.length) {
    return summaryProperty.map((item) => {
      if (convert && 'source_id' in file && file.source_id) {
        if (item.source_id === file.source_id) {
          file.count = item.count
          file.groups = item.groups
        }
      } else {
        if (item.source_id === String(file.id)) {
          file.count = item.count
          file.groups = item.groups
        }
      }
    })
  }
  return null
}
